import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export interface UserPreferences {
  id: string;
  user_id: string;
  theme_preference: 'light' | 'dark' | 'system';
  language_preference: string;
  session_timeout_minutes?: number;
  sidebar_theme?: string;
  notification_settings: {
    email_notifications: boolean;
    browser_notifications: boolean;
    daily_digest: boolean;
    show_daily_popup: boolean;
    notification_types: {
      project_status_change: boolean;
      task_assigned: boolean;
      task_completed: boolean;
      note_added: boolean;
      deadline_request: boolean;
      deadline_decision: boolean;
      user_added_to_project: boolean;
    };
  };
  last_daily_popup_date: string | null;
  created_at: string;
  updated_at: string;
}

export const useUserPreferences = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  return useQuery({
    queryKey: ['user-preferences', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        toast({
          title: "Erro ao carregar preferências",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }

      return data as UserPreferences | null;
    },
    enabled: !!user?.id,
  });
};

export const useUpdateUserPreferences = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preferences: Partial<UserPreferences['notification_settings']> & { activity_preferences?: string[] }) => {
      if (!user?.id) throw new Error('Usuário não autenticado');

      // Primeiro, tenta buscar configurações existentes
      const { data: existingData } = await supabase
        .from('user_preferences')
        .select('notification_settings')
        .eq('user_id', user.id)
        .single();

      const defaultSettings = {
        email_notifications: false,
        browser_notifications: true,
        daily_digest: false,
        show_daily_popup: true,
        notification_types: {
          project_status_change: true,
          task_assigned: true,
          task_completed: true,
          note_added: true,
          deadline_request: true,
          deadline_decision: true,
          user_added_to_project: true,
        },
        activity_preferences: Object.keys({
          'PROJECT_STATUS_CHANGE': true,
          'CONTRACT_TASK_COMPLETED': true,
          'DEADLINE_APPROVED': true,
          'CONTRACT_CREATED': true,
          'PROJECT_CREATED': true,
          'NOTE_ADDED': true,
          'TASK_COMPLETED': true,
          'TEAM_MEMBER_ADDED': true,
          'RISK_ADDED': true,
        })
      };

      const currentSettings = (existingData?.notification_settings as any) || defaultSettings;

      const updatedSettings = {
        ...currentSettings,
        ...preferences
      };

      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          notification_settings: updatedSettings,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences', user?.id] });
      toast({
        title: "Preferências atualizadas",
        description: "Suas configurações foram salvas com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao salvar preferências",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateNotificationType = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, enabled }: { type: string; enabled: boolean }) => {
      if (!user?.id) throw new Error('Usuário não autenticado');

      // Busca as configurações atuais
      const { data: existingData } = await supabase
        .from('user_preferences')
        .select('notification_settings')
        .eq('user_id', user.id)
        .single();

      const defaultSettings = {
        email_notifications: false,
        browser_notifications: true,
        daily_digest: false,
        show_daily_popup: true,
        notification_types: {
          project_status_change: true,
          task_assigned: true,
          task_completed: true,
          note_added: true,
          deadline_request: true,
          deadline_decision: true,
          user_added_to_project: true,
        }
      };

      const currentSettings = (existingData?.notification_settings as any) || defaultSettings;

      const updatedSettings = {
        ...currentSettings,
        notification_types: {
          ...currentSettings.notification_types,
          [type]: enabled
        }
      };

      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          notification_settings: updatedSettings,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences', user?.id] });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar configuração",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useUpdateSessionTimeout = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (timeoutMinutes: number) => {
      if (!user?.id) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({
          user_id: user.id,
          session_timeout_minutes: timeoutMinutes,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences', user?.id] });
      toast({
        title: "Timeout de sessão atualizado",
        description: "A configuração foi salva com sucesso.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao atualizar timeout",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};